import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Phone, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { key: "Ushers", testid: "form-checkbox-role-ushers" },
  { key: "Ticket Scanners", testid: "form-checkbox-role-scanners" },
  { key: "VIP Hospitality", testid: "form-checkbox-role-hospitality" },
  { key: "Concessions", testid: "form-checkbox-role-concessions" },
  { key: "Crowd Safety", testid: "form-checkbox-role-safety" },
  { key: "Stagehands", testid: "form-checkbox-role-stagehands" },
  { key: "Retail Brand Ambassador", testid: "form-checkbox-role-retail" },
];

const initial = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  event_type: "",
  event_name: "",
  location: "",
  event_date: "",
  attendance: "",
  staff_count: "",
  notes: "",
};

export default function RequestTalentForm({ prefill, onJoinCrew }) {
  const [form, setForm] = useState(initial);
  const [roles, setRoles] = useState([]);
  const [crewSplit, setCrewSplit] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        event_type: prefill.eventType || f.event_type,
        event_name: prefill.eventName || f.event_name,
        staff_count: prefill.staffCount || f.staff_count,
      }));
      setRoles(prefill.roles || []);
      setCrewSplit(prefill.crewSplit || {});
    }
  }, [prefill]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const toggleRole = (r) =>
    setRoles((rs) =>
      rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !form.event_name) {
      toast.error("Please fill in company, contact name, email, phone, and event name.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        lead_type: "client_quote",
        roles_needed: roles,
        crew_split: crewSplit,
      });
      toast.success(
        "Quote request received! Our Operations Captain will contact you within 2 business hours."
      );
      setForm(initial);
      setRoles([]);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0F111A] border-white/10 text-white placeholder:text-slate-500 rounded-sm focus-visible:ring-sky-500";

  return (
    <section id="request" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2"
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono-tactical font-semibold text-sky-400">
            Request Talent
          </p>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
            Get Your Crew Quote
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Tell us about your event and an Operations Captain will respond
            with a custom roster blueprint and pricing — usually within 2
            business hours.
          </p>
          <div className="mt-8 space-y-4">
            <a
              href="tel:+61430941010"
              data-testid="request-phone-link"
              className="flex items-center gap-3 text-slate-300 hover:text-sky-400 transition-colors"
            >
              <span className="w-10 h-10 rounded-sm bg-sky-500/10 border border-sky-500/25 flex items-center justify-center">
                <Phone className="w-4 h-4 text-sky-500" />
              </span>
              <span className="font-mono-tactical">0430 941 010 — 24/7 Ops Desk</span>
            </a>
            <a
              href="mailto:josephatsora@gmail.com"
              data-testid="request-email-link"
              className="flex items-center gap-3 text-slate-300 hover:text-sky-400 transition-colors"
            >
              <span className="w-10 h-10 rounded-sm bg-sky-500/10 border border-sky-500/25 flex items-center justify-center">
                <Mail className="w-4 h-4 text-sky-500" />
              </span>
              josephatsora@gmail.com
            </a>
          </div>
          <p className="mt-10 text-sm text-slate-500">
            Looking for work instead?{" "}
            <button
              data-testid="job-seeker-modal-trigger"
              onClick={onJoinCrew}
              className="text-sky-400 hover:text-sky-300 font-semibold underline underline-offset-4"
            >
              Apply to join the crew
            </button>
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="request-talent-form"
          onSubmit={submit}
          className="lg:col-span-3 bg-[#131622] border border-white/10 rounded-md p-6 sm:p-8"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              data-testid="form-input-company"
              placeholder="Organization / Company *"
              value={form.company_name}
              onChange={set("company_name")}
              className={inputCls}
            />
            <Input
              data-testid="form-input-contact-name"
              placeholder="Contact Full Name *"
              value={form.contact_name}
              onChange={set("contact_name")}
              className={inputCls}
            />
            <Input
              data-testid="form-input-email"
              type="email"
              placeholder="Work Email *"
              value={form.email}
              onChange={set("email")}
              className={inputCls}
            />
            <Input
              data-testid="form-input-phone"
              placeholder="Phone Number *"
              value={form.phone}
              onChange={set("phone")}
              className={inputCls}
            />
            <Input
              data-testid="form-input-event-name"
              placeholder="Event Name *"
              value={form.event_name}
              onChange={set("event_name")}
              className={inputCls}
            />
            <Select
              value={form.event_type}
              onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}
            >
              <SelectTrigger
                data-testid="form-select-event-type"
                className={inputCls}
              >
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#131622] border-white/10 text-white">
                <SelectItem value="concert">Concert / Arena Show</SelectItem>
                <SelectItem value="sports">Sports Game</SelectItem>
                <SelectItem value="festival">Festival</SelectItem>
                <SelectItem value="gala">Gala / Corporate</SelectItem>
                <SelectItem value="retail">Retail Brand Ambassador / Fashion</SelectItem>
                <SelectItem value="conference">Conference / Expo</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="form-input-location"
              placeholder="Venue / City"
              value={form.location}
              onChange={set("location")}
              className={inputCls}
            />
            <Input
              data-testid="form-input-event-date"
              type="date"
              value={form.event_date}
              onChange={set("event_date")}
              className={`${inputCls} [color-scheme:dark]`}
            />
            <Input
              data-testid="form-input-attendance"
              placeholder="Estimated Attendance"
              value={form.attendance}
              onChange={set("attendance")}
              className={inputCls}
            />
            <Select
              value={form.staff_count}
              onValueChange={(v) => setForm((f) => ({ ...f, staff_count: v }))}
            >
              <SelectTrigger
                data-testid="form-input-staff-count"
                className={inputCls}
              >
                <SelectValue placeholder="Staff Needed" />
              </SelectTrigger>
              <SelectContent className="bg-[#131622] border-white/10 text-white">
                <SelectItem value="5-15">5 – 15</SelectItem>
                <SelectItem value="16-50">16 – 50</SelectItem>
                <SelectItem value="51-100">51 – 100</SelectItem>
                <SelectItem value="100+">100+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="mt-6 mb-3 text-sm font-semibold text-slate-300">
            Roles Required
          </p>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((r) => (
              <button
                type="button"
                key={r.key}
                data-testid={r.testid}
                onClick={() => toggleRole(r.key)}
                className={`text-sm rounded-full px-4 py-2 border transition-colors duration-200 ${
                  roles.includes(r.key)
                    ? "bg-sky-500 text-[#090A0F] border-sky-500 font-semibold"
                    : "border-white/15 text-slate-300 hover:border-sky-500/50 hover:text-sky-400"
                }`}
              >
                {r.key}
              </button>
            ))}
          </div>

          <Textarea
            data-testid="form-input-notes"
            placeholder="Special briefing / notes (uniforms, shift times, gate map...)"
            value={form.notes}
            onChange={set("notes")}
            className={`${inputCls} mt-6 min-h-[110px]`}
          />

          <button
            data-testid="form-submit-button"
            type="submit"
            disabled={loading}
            className="group mt-6 w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-6 py-3.5 transition-colors duration-200"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {loading ? "Sending..." : "Request My Quote"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
